import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, ChartDataset, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { PdvSummary } from 'src/app/models/ConsumptionTicket/ConsumptionTicketDashboard.model';
import { CHART_COLORS } from '../chart-utils/chart-colors.const';
import { observeChartResize } from '../chart-utils/chart-resize.util';

interface BarEndDataset extends ChartDataset<'bar'> {
  percentages?: number[];
}

const BAR_END_VALUE_PLUGIN: Plugin<'bar'> = {
  id: 'barEndValue',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      const percentages = (dataset as BarEndDataset).percentages;

      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        if (value === null || value === undefined) return;
        const { x, y } = element.getProps(['x', 'y'], true);

        ctx.save();
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        ctx.font = '600 11px sans-serif';
        ctx.fillStyle = '#3B3B3B';
        const valueText = String(value);
        ctx.fillText(valueText, x + 4, y);

        const percent = percentages?.[index];
        if (percent !== undefined) {
          const valueWidth = ctx.measureText(valueText).width;
          ctx.font = '400 10px sans-serif';
          ctx.fillStyle = '#8A8A8A';
          ctx.fillText(`(${percent.toFixed(2)}%)`, x + 4 + valueWidth + 4, y);
        }

        ctx.restore();
      });
    });
  }
};

const VALUE_END_PADDING_RIGHT = 52;

@Component({
  selector: 'app-consumption-ticket-dashboard-pdv-chart',
  templateUrl: './consumption-ticket-dashboard-pdv-chart.component.html',
  styleUrls: ['./consumption-ticket-dashboard-pdv-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConsumptionTicketDashboardPdvChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() pdvAverages: PdvSummary[] = [];

  @ViewChild('topAxisCanvas', { read: BaseChartDirective }) private topAxisChartDirective?: BaseChartDirective;
  @ViewChild('barsCanvas', { read: BaseChartDirective }) private chartDirective?: BaseChartDirective;
  @ViewChild('bottomAxisCanvas', { read: BaseChartDirective }) private bottomAxisChartDirective?: BaseChartDirective;

  barChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  barChartOptions: ChartConfiguration['options'];
  barChartPlugins: Plugin<'bar'>[] = [];

  readonly axisChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  topAxisOptions: ChartConfiguration['options'];
  bottomAxisOptions: ChartConfiguration['options'];

  private stopResizeObservers: (() => void)[] = [];

  constructor(private translateService: TranslateService, private elementRef: ElementRef<HTMLElement>) {
    this.barChartOptions = this.buildChartOptions(1);
    this.topAxisOptions = this.buildAxisOptions(1, 'top', false);
    this.bottomAxisOptions = this.buildAxisOptions(1, 'bottom', true);
    this.barChartPlugins = [BAR_END_VALUE_PLUGIN, this.buildSyncRulerPaddingPlugin()];
  }

  ngAfterViewInit(): void {
    this.stopResizeObservers = [
      observeChartResize(this.elementRef.nativeElement, () => this.topAxisChartDirective),
      observeChartResize(this.elementRef.nativeElement, () => this.chartDirective),
      observeChartResize(this.elementRef.nativeElement, () => this.bottomAxisChartDirective)
    ];
  }

  ngOnDestroy(): void {
    this.stopResizeObservers.forEach(stop => stop());
  }

  ngOnChanges(): void {
    this.buildChartData();
  }

  get hasData(): boolean {
    return this.pdvAverages.length > 0;
  }

  get chartHeight(): number {
    return Math.max(220, this.pdvAverages.length * 56 + 40);
  }

  private buildChartData(): void {
    const labels = this.pdvAverages.map(pdv => pdv.pdvDescription);

    const datasets: BarEndDataset[] = [
      {
        label: this.translateService.instant('consumptionTicketSales.dashboard.ticketsIssued'),
        data: this.pdvAverages.map(pdv => pdv.ticketCount),
        percentages: this.pdvAverages.map(pdv => pdv.ticketPercent),
        backgroundColor: CHART_COLORS.blue,
        borderRadius: 4,
        maxBarThickness: 22,
        barPercentage: 0.6,
        categoryPercentage: 0.7
      },
      {
        label: this.translateService.instant('consumptionTicketSales.dashboard.salesCount'),
        data: this.pdvAverages.map(pdv => pdv.salesCount),
        percentages: this.pdvAverages.map(pdv => pdv.salesPercent),
        backgroundColor: CHART_COLORS.green,
        borderRadius: 4,
        maxBarThickness: 22,
        barPercentage: 0.6,
        categoryPercentage: 0.7
      }
    ];

    this.barChartData = { labels, datasets };

    const rawMax = this.pdvAverages.reduce((max, pdv) => Math.max(max, pdv.ticketCount, pdv.salesCount), 1);
    const maxValue = Math.ceil(rawMax * 1.2);
    this.barChartOptions = this.buildChartOptions(maxValue);
    this.topAxisOptions = this.buildAxisOptions(maxValue, 'top', false);
    this.bottomAxisOptions = this.buildAxisOptions(maxValue, 'bottom', true);
  }

  private buildSyncRulerPaddingPlugin(): Plugin<'bar'> {
    return {
      id: 'syncRulerPadding',
      afterLayout: (chart) => {
        const left = chart.chartArea?.left;
        if (left === undefined) return;
        this.applyRulerPadding(this.topAxisChartDirective, left);
        this.applyRulerPadding(this.bottomAxisChartDirective, left);
      }
    };
  }

  private applyRulerPadding(directive: BaseChartDirective | undefined, left: number): void {
    const rulerChart = directive?.chart as Chart<'bar'> | undefined;
    if (!rulerChart) return;

    const currentPadding = rulerChart.options.layout?.padding;
    const currentLeft = typeof currentPadding === 'object' ? (currentPadding as { left?: number }).left : undefined;
    if (currentLeft === left) return;

    rulerChart.options.layout = {
      ...rulerChart.options.layout,
      padding: { ...(typeof currentPadding === 'object' ? currentPadding : {}), left, right: VALUE_END_PADDING_RIGHT }
    };
    rulerChart.update('none');
  }

  private buildAxisOptions(maxValue: number, position: 'top' | 'bottom', showGrid: boolean): ChartConfiguration['options'] {
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'linear',
          position,
          beginAtZero: true,
          max: maxValue,
          ticks: { precision: 0 },
          grid: { display: showGrid, color: '#F0F0F0' },
          border: { display: false }
        },
        y: { display: false }
      },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      layout: { padding: { left: 0, right: VALUE_END_PADDING_RIGHT } }
    };
  }

  private buildChartOptions(maxValue: number): ChartConfiguration['options'] {
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'linear',
          beginAtZero: true,
          max: maxValue,
          ticks: { precision: 0, display: false },
          grid: { color: '#F0F0F0' },
          border: { display: false }
        },
        y: { grid: { display: false }, ticks: { color: '#505050' } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const pdv = this.pdvAverages[context.dataIndex];
              const percentage = context.datasetIndex === 0 ? pdv.ticketPercent : pdv.salesPercent;
              return `${context.dataset.label}: ${context.formattedValue} (${percentage.toFixed(2)}%)`;
            },
            footer: (items) => {
              const pdv = this.pdvAverages[items[0].dataIndex];
              const average = this.translateService.instant('consumptionTicketSales.dashboard.averageTicketsPerSale');
              return `${average}: ${pdv.averageTicketsPerSale.toFixed(1)}`;
            }
          }
        }
      },
      layout: {
        padding: { bottom: 4, right: VALUE_END_PADDING_RIGHT }
      }
    };
  }
}
